import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ComplianceDecision } from '@shared/api/contracts/compliance';
import type { ReasonFactorValue } from '@shared/ui';
import { Button, ReasonFactorDialog, Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TypographyMuted } from '@shared/ui';
import { decisionStateKey, formatRiskScore } from '@entities/compliance';
import { formatDateTime } from '@shared/lib/format-date';

interface PendingDecisionsPanelProps {
  items: ComplianceDecision[];
  userId: number | null;
  loading?: boolean;
  onOpenWithdrawal: (id: string) => void;
  onResolve: (id: string, action: 'confirm' | 'reject' | 'cancel', value: ReasonFactorValue) => Promise<boolean>;
}

export function PendingDecisionsPanel({ items, userId, loading, onOpenWithdrawal, onResolve }: PendingDecisionsPanelProps) {
  const { t } = useTranslation('cabinet');
  const [target, setTarget] = useState<{ id: string; action: 'confirm' | 'reject' | 'cancel' } | null>(null);

  if (!items.length) return <TypographyMuted className="block py-10 text-center">{t('compliance.pending.empty')}</TypographyMuted>;

  return (
    <div className="surface-card min-w-0 space-y-3">
      <div className="min-w-0 overflow-x-auto">
        <Table className="min-w-[48rem]">
          <TableHeader>
            <TableRow>
              <TableHead>{t('compliance.columns.target')}</TableHead>
              <TableHead>{t('compliance.columns.decision')}</TableHead>
              <TableHead>{t('compliance.columns.reason')}</TableHead>
              <TableHead>{t('withdrawals.columns.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => {
              const author = userId !== null && item.actorUserId === userId;
              return (
                <TableRow
                  key={item.id}
                  className={item.targetType === 'WITHDRAWAL' ? 'cursor-pointer' : undefined}
                  onClick={() => {
                    if (item.targetType === 'WITHDRAWAL') onOpenWithdrawal(item.targetId);
                  }}
                >
                  <TableCell>
                    {item.targetType === 'WITHDRAWAL' ? `#${item.targetId}` : t('compliance.address.target', { id: item.targetId })}
                  </TableCell>
                  <TableCell>
                    {t(`compliance.decisions.${item.decision}.confirm`, { defaultValue: item.decision })}
                    <span className="mt-1 block text-xs text-muted-foreground">
                      {t(decisionStateKey(item.state))} · {formatRiskScore(item.riskscore)} · {formatDateTime(item.createdAt)}
                    </span>
                  </TableCell>
                  <TableCell className="max-w-[16rem] whitespace-pre-wrap break-words">{item.reason}</TableCell>
                  <TableCell onClick={(event) => event.stopPropagation()}>
                    <div className="flex flex-col gap-2 sm:flex-row">
                      {author ? (
                        <Button type="button" size="sm" variant="outline" disabled={loading} onClick={() => setTarget({ id: item.id, action: 'cancel' })}>
                          {t('compliance.pending.cancel')}
                        </Button>
                      ) : (
                        <>
                          <Button type="button" size="sm" disabled={loading} onClick={() => setTarget({ id: item.id, action: 'confirm' })}>
                            {t('compliance.pending.confirm')}
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="destructive"
                            disabled={loading}
                            onClick={() => setTarget({ id: item.id, action: 'reject' })}
                          >
                            {t('compliance.pending.reject')}
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      <ReasonFactorDialog
        open={Boolean(target)}
        title={t(`compliance.pending.${target?.action ?? 'confirm'}Title`)}
        description={t(`compliance.pending.${target?.action ?? 'confirm'}Description`)}
        confirmLabel={t(`compliance.pending.${target?.action ?? 'confirm'}`)}
        confirmVariant={target?.action === 'reject' ? 'destructive' : 'default'}
        reasonRequired={target?.action !== 'confirm'}
        loading={loading}
        onOpenChange={(open) => {
          if (!open) setTarget(null);
        }}
        onConfirm={(value) => {
          if (!target) return;
          void onResolve(target.id, target.action, value).then((ok) => {
            if (ok) setTarget(null);
          });
        }}
      />
    </div>
  );
}
